import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useNavFlags } from "@/hooks/use-nav-flags";
import { navFlagKeys, navItemVisible, type NavConfig, type NavGroup, type NavItem } from "@/config/navigation";

const STORAGE_PREFIX = "gfi.sidebar.collapsed.";

function getStoredOpen(groupId: string): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + groupId);
    return stored === null ? true : stored !== "true";
  } catch {
    return true;
  }
}

function saveOpen(groupId: string, open: boolean) {
  try {
    localStorage.setItem(STORAGE_PREFIX + groupId, (!open).toString());
  } catch {
    // localStorage pode falhar (modo privado, cota cheia). Persistir o estado do
    // menu é conveniência: se não der, a sidebar apenas volta ao padrão.
  }
}

export interface SidebarColorScheme {
  activeBg: string;
  activeText: string;
}

const defaultColors: SidebarColorScheme = {
  activeBg: "bg-sidebar-accent",
  activeText: "text-sidebar-accent-foreground",
};

interface SidebarNavProps {
  config: NavConfig;
  collapsed?: boolean;
  colorScheme?: SidebarColorScheme;
  onItemClick?: () => void;
}

export function SidebarNav({
  config,
  collapsed = false,
  colorScheme = defaultColors,
  onItemClick,
}: SidebarNavProps) {
  const location = useLocation();
  const { user } = useAuth();
  const flags = useNavFlags(navFlagKeys(config));

  function isActive(item: NavItem): boolean {
    const p = location.pathname;
    if (p === item.route) return true;
    if (item.route === "/conectores" && p.startsWith("/imovel/")) return true;
    const segments = item.route.split("/").filter(Boolean);
    if (segments.length <= 1) return false;
    return p.startsWith(item.route);
  }

  function filterItems(items: NavItem[]): NavItem[] {
    return items.filter((item) => navItemVisible(item, { email: user?.email, flags }));
  }

  return (
    <nav aria-label="Navegação principal" role="navigation" className="flex-1 px-2 py-2 overflow-y-auto">
      {config.groups.map((group) => {
        const visible = filterItems(group.items);
        if (visible.length === 0) return null;
        return (
          <NavGroupSection
            key={group.id}
            group={group}
            visibleItems={visible}
            collapsed={collapsed}
            colorScheme={colorScheme}
            isActive={isActive}
            onItemClick={onItemClick}
          />
        );
      })}
    </nav>
  );
}

interface NavGroupSectionProps {
  group: NavGroup;
  visibleItems: NavItem[];
  collapsed: boolean;
  colorScheme: SidebarColorScheme;
  isActive: (item: NavItem) => boolean;
  onItemClick?: () => void;
}

function NavGroupSection({
  group,
  visibleItems,
  collapsed,
  colorScheme,
  isActive,
  onItemClick,
}: NavGroupSectionProps) {
  const [open, setOpen] = useState(() => getStoredOpen(group.id));

  function handleOpenChange(next: boolean) {
    setOpen(next);
    saveOpen(group.id, next);
  }

  if (collapsed) {
    return (
      <div className="mb-1 space-y-0.5">
        {visibleItems.map((item) => (
          <NavItemLink
            key={item.id}
            item={item}
            active={isActive(item)}
            collapsed
            colorScheme={colorScheme}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange} className="mb-3">
      <CollapsibleTrigger asChild>
        <button
          aria-expanded={open}
          className="flex items-center justify-between w-full px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-muted-foreground/60 hover:text-muted-foreground transition-colors"
        >
          <span>{group.label}</span>
          <ChevronRight
            className={cn(
              "h-3 w-3 transition-transform duration-150 shrink-0",
              open && "rotate-90"
            )}
            aria-hidden="true"
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-0.5 space-y-0.5">
        {visibleItems.map((item) => (
          <NavItemLink
            key={item.id}
            item={item}
            active={isActive(item)}
            collapsed={false}
            colorScheme={colorScheme}
            onItemClick={onItemClick}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface NavItemLinkProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  colorScheme: SidebarColorScheme;
  onItemClick?: () => void;
}

function NavItemLink({ item, active, collapsed, colorScheme, onItemClick }: NavItemLinkProps) {
  return (
    <Link
      to={item.route}
      role="menuitem"
      data-tour-nav={item.id}
      title={collapsed ? item.label : undefined}
      aria-current={active ? "page" : undefined}
      onClick={onItemClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors",
        active
          ? `${colorScheme.activeBg} ${colorScheme.activeText}`
          : "text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/50"
      )}
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}
